const PREFIX = 'manifesto:openTabs:'

export interface PersistedTabs {
  filenames: string[]
  activeFilename?: string
  /**
   * Open tabs in most-recently-used order, current first. Persisted because
   * ⌥Tab is recency-ordered: without it a reload leaves the switcher walking the
   * tab bar left-to-right, and the "go back to the file I was just in" tap lands
   * on whichever tab happens to sit first.
   */
  mru?: string[]
}

interface LegacyPersistedTabs {
  pinned?: string[]
  preview?: string
  activeFilename?: string
}

function storageKey(bookId: string): string {
  return `${PREFIX}${bookId}`
}

export function readPersistedTabs(bookId: string): PersistedTabs | null {
  try {
    const raw = localStorage.getItem(storageKey(bookId))
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as PersistedTabs | LegacyPersistedTabs
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    if (Array.isArray((parsed as PersistedTabs).filenames)) {
      const p = parsed as PersistedTabs
      return {
        filenames: p.filenames,
        activeFilename: p.activeFilename,
        mru: Array.isArray(p.mru) ? p.mru : undefined,
      }
    }
    const legacy = parsed as LegacyPersistedTabs
    if (Array.isArray(legacy.pinned)) {
      const filenames = [...legacy.pinned]
      if (legacy.preview && !filenames.includes(legacy.preview)) {
        filenames.push(legacy.preview)
      }
      return { filenames, activeFilename: legacy.activeFilename }
    }
    return null
  } catch {
    return null
  }
}

export function writePersistedTabs(bookId: string, persisted: PersistedTabs): void {
  try {
    localStorage.setItem(storageKey(bookId), JSON.stringify(persisted))
  } catch {
    // quota or private mode
  }
}

export function clearPersistedTabs(bookId: string): void {
  try {
    localStorage.removeItem(storageKey(bookId))
  } catch {
    // ignore
  }
}
